import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CampaignsSlice } from "types/campaign";
import { ERROR } from "types";
import { fetchCampaign, fetchCampaignsCount, fetchCampaignsList, markCampaignCompleted, sendCampaignForApprovalRequest } from "./thunk";

const initialState: CampaignsSlice = {
    filters: {
        page: 1,
        status: 'approved',
    },
    applied_filter_id: null,
    filtered_lists: {},
    items: {},
    totals: {},
    loading: false,
    error: null,
};

const campaigns = createSlice({
    name: 'campaigns',
    initialState,
    reducers: {
        updateCampaignsFilters: (state, action) => {
            state.filters = action.payload;
        },
        markCampaignDataExpired: (state, action: PayloadAction<{ campaignId: string }>) => {
            const { campaignId } = action.payload;
            if (state.items[campaignId]) {
                state.items[campaignId].expired = true;
            }
        },
        updateRemainingBudget: (state, action: PayloadAction<{ campaignId: string; budget: string | number }>) => {
            const { campaignId, budget } = action.payload;
            const item = state.items[campaignId];
            if (!item?.data?.campaign) return;

            state.items[campaignId] = {
                ...item,
                data: {
                    ...item.data,
                    campaign: {
                        ...item.data.campaign,
                        remaining_budget: budget,
                    },
                },
            };
        },
        refetchCampaignLists: (state) => {
            state.filtered_lists = {};
            state.totals = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaignsList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCampaignsList.fulfilled, (state, { payload }) => {
                const { filters, filterId, campaigns } = payload;
                const existingList = state.filtered_lists?.[filterId]?.list ?? [];
                const list = filters.page === 1 ? campaigns.items : [...existingList, ...campaigns.items];

                state.filtered_lists = {
                    ...state.filtered_lists,
                    [filterId]: {
                        list,
                        last_fetched_at: Date.now(),
                        page: filters.page,
                        has_more: campaigns.count > list.length,
                        count: campaigns.count,
                        expired: false,
                    },
                };

                state.applied_filter_id = filterId;
                state.loading = false;
                state.error = null;
            })
            .addCase(fetchCampaignsList.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED) {
                    state.error = action.payload || 'Failed to fetch campaigns';
                }
            })
            .addCase(fetchCampaignsCount.fulfilled, (state, { payload }) => {
                const { totals, filterKey } = payload;
                state.totals[filterKey] = {
                    data: totals,
                    last_fetched_at: Date.now(),
                };
            })
            .addCase(fetchCampaign.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCampaign.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.items[action.payload.campaignId] = {
                    data: action.payload.campaignData,
                    last_fetched_at: Date.now(),
                    expired: false,
                };
            })
            .addCase(fetchCampaign.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED) {
                    state.error = action.payload || "Failed to fetch campaign!";
                }
            })
            .addCase(markCampaignCompleted.fulfilled, (state, { payload }) => {
                const { campaignId } = payload;
                const item = state.items[campaignId];
                if (!item?.data?.campaign) return;

                state.items[campaignId] = {
                    ...item,
                    data: {
                        ...item.data,
                        campaign: {
                            ...item.data.campaign,
                            status: 'completed',
                        },
                    },
                };
            })
            .addCase(sendCampaignForApprovalRequest.fulfilled, (state, { payload }) => {
                const { campaignId } = payload;
                const item = state.items[campaignId];

                state.items[campaignId] = {
                    ...(item ?? { last_fetched_at: null, expired: false }),
                    data: {
                        ...(item?.data ?? { share_url: '', brand: { img_url: null } }),
                        campaign: {
                            ...(item?.data?.campaign ?? {}),
                            status: 'pending',
                        },
                    },
                };
            });
    },
});

export const { updateCampaignsFilters, markCampaignDataExpired, updateRemainingBudget, refetchCampaignLists } = campaigns.actions;

export default campaigns.reducer;
